import { useState } from 'react'
import { Dialog } from 'radix-ui'
import { useCreateRole, useUpdateRole } from '../hooks'
import type { Role } from '../types'
import { formatError } from './formatError'

const CODE_REGEX = /^[a-z][a-z0-9_]{0,63}$/

export function RoleFormDialog({ editing, onClose }: {
  editing: Role | null
  onClose: () => void
}) {
  const create = useCreateRole()
  const update = useUpdateRole()
  const [code, setCode] = useState(editing?.code ?? '')
  const [name, setName] = useState(editing?.name ?? '')
  const [description, setDescription] = useState(editing?.description ?? '')
  const [isActive, setIsActive] = useState(editing?.isActive ?? true)
  const [error, setError] = useState<string | null>(null)

  const submit = () => {
    setError(null)
    if (!editing && !CODE_REGEX.test(code)) {
      setError('Kod: małe litery, cyfry, podkreślenia. Musi zaczynać się od litery.')
      return
    }
    if (!name.trim()) { setError('Nazwa nie może być pusta.'); return }

    if (editing) {
      update.mutate(
        { code: editing.code, payload: { name, description, isActive } },
        { onSuccess: onClose, onError: e => setError(formatError(e)) },
      )
    } else {
      create.mutate(
        { code, name, description, isActive },
        { onSuccess: onClose, onError: e => setError(formatError(e)) },
      )
    }
  }

  const isSubmitting = create.isPending || update.isPending

  return (
    <Dialog.Root open onOpenChange={o => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-5 w-[440px] shadow-xl">
          <Dialog.Title className="text-base font-semibold">
            {editing ? `Edycja roli ${editing.code}` : 'Nowa rola'}
          </Dialog.Title>
          <Dialog.Description className="text-xs text-slate-500 mt-1">
            Kod jest stabilnym identyfikatorem (referencowany w JWT i seedach), nie zmienia się po utworzeniu.
          </Dialog.Description>

          <div className="mt-4 flex flex-col gap-3 text-sm">
            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Kod</span>
              <input
                value={code}
                onChange={e => setCode(e.target.value.toLowerCase())}
                disabled={!!editing || isSubmitting}
                className="border rounded px-2 py-1 font-mono"
                placeholder="np. dyrektor_handlowy"
              />
            </label>
            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Nazwa</span>
              <input value={name} onChange={e => setName(e.target.value)} disabled={isSubmitting} className="border rounded px-2 py-1" />
            </label>
            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Opis</span>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                disabled={isSubmitting}
                rows={3}
                className="border rounded px-2 py-1 resize-y"
                placeholder="Krótki opis - co rola robi, kogo dotyczy..."
              />
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} disabled={isSubmitting} />
              <span>Aktywna</span>
            </label>

            {error && <p className="text-red-600 text-xs">{error}</p>}

            <div className="flex justify-end gap-2 mt-2">
              <button type="button" onClick={onClose} className="px-3 py-1.5 border rounded" disabled={isSubmitting}>Anuluj</button>
              <button type="button" onClick={submit} className="px-3 py-1.5 bg-sky-600 text-white rounded disabled:opacity-50" disabled={isSubmitting}>
                {isSubmitting ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
